/** EntityFormModal — generic Create/Edit modal for catalog entities.
 *  Field schema is driven per-kind below. Price is entered in INR (rupees) and converted to paise.
 */
import { useState, useEffect } from "react";
import { X, Image as ImageIcon, Upload, Check } from "lucide-react";
import { Button, Card, CardBody, Eyebrow } from "../../components/ui";
import { api } from "../../lib/api";

// ===== Media picker (inline, lightweight) — supports single and multi-select =====
function MediaPickerField({ value, onChange, testId, multi = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const list = multi ? (Array.isArray(value) ? value : []) : [];
  const load = () => api.get("/media?limit=120").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));

  const resolveUrl = (m) => {
    if (!m) return "";
    if (m.url && m.url.startsWith("http")) return m.url;
    if (m.url) return apiBase + m.url;
    return "";
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = resolveUrl(r.data);
      if (url) {
        if (multi) onChange([...list, url]);
        else onChange(url);
      }
      load();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const toggleItem = (url) => {
    if (multi) {
      const has = list.includes(url);
      onChange(has ? list.filter((u) => u !== url) : [...list, url]);
    } else {
      onChange(url);
      setOpen(false);
    }
  };

  const removeFromList = (url) => onChange(list.filter((u) => u !== url));

  return (
    <div data-testid={testId}>
      <div className="flex items-stretch gap-2">
        {!multi && (
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste image URL or open Media Library →"
            className="flex-1 bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13.5px] focus:outline-none focus:border-mauve" />
        )}
        {multi && (
          <div className="flex-1 min-h-[44px] bg-surface border border-line-2 rounded-xl px-3 py-2 text-[13px] text-text-dim flex items-center">
            {list.length > 0 ? `${list.length} image${list.length === 1 ? "" : "s"} selected — click thumbnails below to remove` : "No images selected — open Library or upload →"}
          </div>
        )}
        <Button type="button" size="sm" variant="ghost" onClick={() => { setOpen((o) => !o); if (!items) load(); }} data-testid={`${testId}-open`}>
          <ImageIcon size={14} /> Library
        </Button>
        <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-line-2 bg-surface text-[12.5px] cursor-pointer hover:border-mauve">
          <Upload size={13} /> {uploading ? "…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={`${testId}-upload`} />
        </label>
      </div>
      {!multi && value && (
        <div className="mt-2 inline-block rounded-lg overflow-hidden border border-line-2">
          <img src={value} alt="" style={{ width: 100, height: 70, objectFit: "cover", display: "block" }} />
        </div>
      )}
      {multi && list.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testId}-selected`}>
          {list.map((u, i) => (
            <button key={i} type="button" onClick={() => removeFromList(u)} title="Click to remove"
              className="relative w-[70px] h-[70px] rounded-lg overflow-hidden border border-line-2 hover:border-red-400 group">
              <img src={u} alt="" className="w-full h-full object-cover" />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center text-white opacity-0 group-hover:opacity-100 text-[11px]">Remove</span>
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-3 p-3 rounded-xl bg-surface border border-line-2 max-h-[280px] overflow-auto">
          {!items && <p className="text-text-faint text-[12px]">Loading library…</p>}
          {items && items.length === 0 && <p className="text-text-faint text-[12.5px]">No media yet. Upload above or via Admin → Media Library.</p>}
          {items && items.length > 0 && (
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {items.filter((m) => m.kind === "image" || /image/i.test(m.mime_type || "")).map((m) => {
                const url = resolveUrl(m);
                const picked = multi ? list.includes(url) : value === url;
                return (
                  <button key={m.id} type="button" onClick={() => toggleItem(url)}
                    className={`relative rounded-lg overflow-hidden border ${picked ? "border-mauve ring-2 ring-mauve" : "border-line-2 hover:border-mauve"}`}
                    data-testid={`media-pick-${m.id}`} title={m.name}>
                    <img src={url} alt={m.alt || m.name} style={{ width: "100%", aspectRatio: "1/1", objectFit: "cover", display: "block" }} />
                    {picked && <span className="absolute top-1 right-1 bg-mauve text-white rounded-full p-0.5"><Check size={11} /></span>}
                  </button>
                );
              })}
            </div>
          )}
          {multi && (
            <div className="mt-3 text-right">
              <Button type="button" size="sm" onClick={() => setOpen(false)}>Done</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Custom sections editor (title + rich content pairs) =====
function CustomSectionsField({ value, onChange, testId }) {
  const list = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(list.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const add = () => onChange([...list, { title: "", content: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list]; [copy[i], copy[j]] = [copy[j], copy[i]]; onChange(copy);
  };
  return (
    <div data-testid={testId} className="space-y-3">
      {list.length === 0 && (
        <p className="text-[13px] text-text-faint">No custom sections yet. Click <span className="text-mauve">+ Add section</span> to create one — you can name it anything (Material, Battery, Process, etc.).</p>
      )}
      {list.map((s, i) => (
        <div key={i} className="rounded-xl border border-line-2 bg-surface p-3 space-y-2" data-testid={`${testId}-item-${i}`}>
          <div className="flex items-center gap-2">
            <input value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })}
              placeholder="Section title (e.g. Material, Battery, Process)" data-testid={`${testId}-title-${i}`}
              className="flex-1 bg-bg-deep border border-line-2 rounded-lg px-3 py-2 text-[13.5px] focus:outline-none focus:border-mauve" />
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-text-faint hover:text-mauve px-2 disabled:opacity-30" title="Move up">↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="text-text-faint hover:text-mauve px-2 disabled:opacity-30" title="Move down">↓</button>
            <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-300 px-2" title="Remove" data-testid={`${testId}-remove-${i}`}>×</button>
          </div>
          <textarea value={s.content || ""} onChange={(e) => update(i, { content: e.target.value })}
            rows={4} placeholder="Section content — plain text or basic HTML. Line breaks are preserved."
            data-testid={`${testId}-content-${i}`}
            className="w-full bg-bg-deep border border-line-2 rounded-lg px-3 py-2 text-[13.5px] focus:outline-none focus:border-mauve font-mono" />
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testId}-add`}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-mauve/50 bg-mauve/10 text-mauve text-[12.5px] hover:bg-mauve/20">
        + Add section
      </button>
    </div>
  );
}

// ===== Category picker (single-select dropdown, stored as list of ids to match backend schema) =====
function CategoryPicker({ path, value, onChange, testId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!path) return;
    api.get(path).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [path]);
  const current = Array.isArray(value) ? value[0] || "" : (value || "");
  return (
    <select value={current} onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
      className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
      data-testid={testId}>
      <option value="">— Select category —</option>
      {(items || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select>
  );
}

// Field definitions per entity kind
const IMAGE_FIELD = { name: "hero_image_url", label: "Hero image", type: "media", help: "Pick from Media Library or paste any URL. Shown on the catalog card and detail page." };
const GALLERY_FIELD = { name: "gallery_image_urls", label: "Gallery images", type: "media_multi", help: "Pick multiple images from the Media Library. Shown as a thumbnail gallery on the detail page." };
const CATEGORY_FIELD = (categoriesPath) => ({ name: "category_ids", label: "Category", type: "category", categoriesPath, help: "Pick a category. Manage categories from Admin → Categories." });
const CUSTOM_SECTIONS_FIELD = { name: "custom_sections", label: "Custom content sections", type: "custom_sections", help: "Add any number of custom sections with your own titles (e.g. Material, Dimensions, Battery, Playback, Process, Deliverables)." };
const SCHEMA = {
  course: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "summary", label: "Summary", type: "textarea", rows: 3 },
    { name: "description_html", label: "Description (HTML)", type: "textarea", rows: 6 },
    IMAGE_FIELD,
    GALLERY_FIELD,
    CATEGORY_FIELD("/courses/categories"),
    { name: "level", label: "Level", type: "select", options: ["foundational", "intermediate", "advanced"] },
    { name: "duration_hours", label: "Duration (hours)", type: "number", step: "0.5" },
    { name: "price_inr_rupees", label: "Price (₹, rupees)", type: "number" },
    { name: "instructor_name", label: "Instructor name", type: "text" },
    { name: "language", label: "Language", type: "text" },
    { name: "learning_outcomes", label: "Learning outcomes (one per line)", type: "textarea", rows: 4, transform: "lines" },
    { name: "curriculum", label: "Curriculum / modules (one per line)", type: "textarea", rows: 4, transform: "lines" },
    { name: "requirements", label: "Requirements (one per line)", type: "textarea", rows: 3, transform: "lines" },
    { name: "faqs", label: "FAQs (one per line, format: Q | A)", type: "textarea", rows: 4, transform: "qa" },
    CUSTOM_SECTIONS_FIELD,
    { name: "is_free", label: "Free course", type: "checkbox" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  sadhana: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "summary", label: "Summary", type: "textarea", rows: 3 },
    { name: "description_html", label: "Description (HTML)", type: "textarea", rows: 6 },
    IMAGE_FIELD,
    GALLERY_FIELD,
    CATEGORY_FIELD("/sadhana/categories"),
    { name: "level", label: "Level", type: "select", options: ["foundational", "intermediate", "advanced"] },
    { name: "duration_days", label: "Duration (days)", type: "number" },
    { name: "deity", label: "Deity", type: "text" },
    { name: "requires_initiation", label: "Requires initiation", type: "checkbox" },
    { name: "price_inr_rupees", label: "Price (₹, rupees)", type: "number" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  product: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "summary", label: "Summary", type: "textarea", rows: 3 },
    { name: "description_html", label: "Description (HTML)", type: "textarea", rows: 6 },
    IMAGE_FIELD,
    GALLERY_FIELD,
    CATEGORY_FIELD("/products/categories"),
    { name: "sku", label: "SKU", type: "text" },
    { name: "stock", label: "Stock qty", type: "number" },
    { name: "price_inr_rupees", label: "Price (₹, rupees)", type: "number" },
    { name: "compare_at_inr_rupees", label: "Compare-at (₹, rupees)", type: "number" },
    { name: "specifications", label: "Specifications (one per line, format: Label | Value)", type: "textarea", rows: 4, transform: "qa" },
    { name: "features", label: "Features (one per line)", type: "textarea", rows: 4, transform: "lines" },
    { name: "whats_included", label: "What's included (one per line)", type: "textarea", rows: 3, transform: "lines" },
    { name: "benefits", label: "Benefits (one per line)", type: "textarea", rows: 3, transform: "lines" },
    CUSTOM_SECTIONS_FIELD,
    { name: "consecrated", label: "Consecrated", type: "checkbox" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  service: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "summary", label: "Summary", type: "textarea", rows: 3 },
    { name: "description_html", label: "Description (HTML)", type: "textarea", rows: 6 },
    IMAGE_FIELD,
    GALLERY_FIELD,
    CATEGORY_FIELD("/services/categories"),
    { name: "duration_minutes", label: "Duration (minutes)", type: "number" },
    { name: "delivery", label: "Delivery mode", type: "select", options: ["online", "in_person", "hybrid"] },
    { name: "price_inr_rupees", label: "Price (₹, rupees)", type: "number" },
    CUSTOM_SECTIONS_FIELD,
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  research: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "abstract", label: "Abstract", type: "textarea", rows: 4 },
    { name: "body_html", label: "Body (HTML)", type: "textarea", rows: 8 },
    IMAGE_FIELD,
    GALLERY_FIELD,
    CATEGORY_FIELD("/research/categories"),
    { name: "journal", label: "Journal", type: "text" },
    { name: "citation", label: "Citation", type: "text" },
    { name: "pdf_url", label: "PDF URL (download link)", type: "text" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  book: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "summary", label: "Summary", type: "textarea", rows: 3 },
    { name: "description_html", label: "Description (HTML)", type: "textarea", rows: 6 },
    IMAGE_FIELD,
    { name: "authors", label: "Authors (comma-separated)", type: "text", transform: "csv" },
    { name: "pages", label: "Pages", type: "number" },
    { name: "language", label: "Language", type: "text" },
    { name: "pdf_url", label: "PDF URL", type: "text" },
    { name: "google_drive_url", label: "Google Drive URL", type: "text" },
    { name: "featured", label: "Featured", type: "checkbox" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
  video: [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "youtube_url", label: "YouTube link", type: "text", required: true, help: "Paste any YouTube video link (youtube.com/watch?v=…, youtu.be/…, /shorts/…). Private or removed videos are rejected." },
    { name: "thumbnail_url", label: "Custom thumbnail (optional)", type: "media", help: "Leave empty to use YouTube's own thumbnail." },
    { name: "description", label: "Description", type: "textarea", rows: 3 },
    { name: "category", label: "Category (optional)", type: "text" },
    { name: "order", label: "Display order (lower shows first)", type: "number" },
    { name: "status", label: "Status", type: "select", options: ["draft", "published", "archived"] },
  ],
};

const KIND_TO_PATH = {
  course: "courses", sadhana: "sadhana", product: "products",
  service: "services", research: "research", book: "books", video: "videos",
};

export function EntityFormModal({ kind, initial, onClose, onSaved }) {
  const fields = SCHEMA[kind] || [];
  const [values, setValues] = useState(() => {
    const v = { ...initial };
    // price_inr is stored as rupees — just mirror it into the rupees-form field
    if (v.price_inr != null) v.price_inr_rupees = v.price_inr;
    if (v.compare_at_inr != null) v.compare_at_inr_rupees = v.compare_at_inr;
    if (Array.isArray(v.authors)) v.authors = v.authors.join(", ");
    return v;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onEsc);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onEsc); document.body.style.overflow = ""; };
  }, [onClose]);

  // Initial pre-fill: arrays back to lines / qa-format for editing
  useEffect(() => {
    setValues((v) => {
      const next = { ...v };
      for (const f of fields) {
        if (f.transform === "lines" && Array.isArray(v[f.name])) next[f.name] = v[f.name].join("\n");
        if (f.transform === "qa" && Array.isArray(v[f.name])) next[f.name] = v[f.name].map((x) => `${x.label || ""} | ${x.value || ""}`).join("\n");
      }
      return next;
    });
  }, []);  // eslint-disable-line

  const update = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const submit = async (e) => {
    e?.preventDefault();
    setErr(null); setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        let val = values[f.name];
        if (val === "" || val === undefined) continue;
        if (f.transform === "csv" && typeof val === "string") val = val.split(",").map((s) => s.trim()).filter(Boolean);
        if (f.transform === "lines" && typeof val === "string") val = val.split("\n").map((s) => s.trim()).filter(Boolean);
        if (f.transform === "qa" && typeof val === "string") {
          val = val.split("\n").map((s) => s.trim()).filter(Boolean).map((line) => {
            const [q, ...a] = line.split("|");
            return { label: (q || "").trim(), value: a.join("|").trim() };
          }).filter((x) => x.label);
        }
        if (f.type === "number" && val !== "") val = Number(val);
        payload[f.name] = val;
      }
      // Map rupee-form fields back to the canonical price_inr / compare_at_inr
      if (payload.price_inr_rupees != null) { payload.price_inr = Math.round(Number(payload.price_inr_rupees)); delete payload.price_inr_rupees; }
      if (payload.compare_at_inr_rupees != null) { payload.compare_at_inr = Math.round(Number(payload.compare_at_inr_rupees)); delete payload.compare_at_inr_rupees; }
      // price_inr is stored as rupees directly (matches what the frontend renders)
      const base = `/admin/${KIND_TO_PATH[kind]}`;
      if (initial?.id) await api.patch(`${base}/${initial.id}`, payload);
      else await api.post(base, payload);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-auto p-6" data-testid="entity-form-modal" role="dialog">
      <Card variant="elevated" className="w-full max-w-2xl my-12">
        <CardBody className="p-7">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Eyebrow>{initial?.id ? "Edit" : "Create"} {kind}</Eyebrow>
              <h2 className="font-display text-[24px] mt-2">{initial?.title || `New ${kind}`}</h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface" aria-label="Close" data-testid="entity-form-close"><X size={18} /></button>
          </div>
          <form onSubmit={submit} className="space-y-4">
            {fields.map((f) => (
              <div key={f.name}>
                <label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea rows={f.rows || 3} value={values[f.name] ?? ""} onChange={(e) => update(f.name, e.target.value)}
                    className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[13.5px] focus:outline-none focus:border-mauve"
                    data-testid={`entity-field-${f.name}`} />
                ) : f.type === "select" ? (
                  <select value={values[f.name] ?? ""} onChange={(e) => update(f.name, e.target.value)}
                    className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
                    data-testid={`entity-field-${f.name}`}>
                    <option value="">— Select —</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-[14px]">
                    <input type="checkbox" checked={!!values[f.name]} onChange={(e) => update(f.name, e.target.checked)} data-testid={`entity-field-${f.name}`} />
                    <span className="text-text-dim">{f.label}</span>
                  </label>
                ) : f.type === "media" ? (
                  <MediaPickerField value={values[f.name] ?? ""} onChange={(v) => update(f.name, v)} testId={`entity-field-${f.name}`} />
                ) : f.type === "media_multi" ? (
                  <MediaPickerField multi value={values[f.name] || []} onChange={(v) => update(f.name, v)} testId={`entity-field-${f.name}`} />
                ) : f.type === "category" ? (
                  <CategoryPicker path={f.categoriesPath} value={values[f.name] || []} onChange={(v) => update(f.name, v)} testId={`entity-field-${f.name}`} />
                ) : f.type === "custom_sections" ? (
                  <CustomSectionsField value={values[f.name] || []} onChange={(v) => update(f.name, v)} testId={`entity-field-${f.name}`} />
                ) : (
                  <input type={f.type} step={f.step} value={values[f.name] ?? ""} onChange={(e) => update(f.name, e.target.value)}
                    className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve"
                    data-testid={`entity-field-${f.name}`} />
                )}
                {f.help && <p className="text-[11.5px] text-text-faint mt-1">{f.help}</p>}
              </div>
            ))}
            {err && <div className="text-[13px] text-red-400 bg-red-500/10 rounded-xl px-4 py-2.5">{err}</div>}
            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={saving} data-testid="entity-form-save">{saving ? "Saving…" : "Save"}</Button>
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
